import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { setAuth } from "../../../features/web/WebAuthentication";
import { getConfiguration } from "../../../features/Global/Configuration";
import DynamicSnackBar from "../../componets/Snackbar/SnackBarChangePassword ";
import {
  MIN_PASSWORD_LENGTH,
  resetPassword,
  errorKeyFromCode,
} from "../../services/passwordResetApi";

const inputClass = (hasError, locale) =>
  `w-full px-3 py-3 placeholder-gray-300 border border-gray-300 rounded-xl focus:outline-none focus:ring focus:ring-pink-100 focus:border-pink-600 ${
    hasError ? "border-rose-400 focus:ring-rose-100 focus:border-rose-300" : ""
  } ${locale}`;

function MyApp() {
  const locale = localStorage.getItem("i18nextLng");
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { phoneNumber, otpCode } = state || {};

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errorKey, setErrorKey] = useState("");

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    dispatch(getConfiguration());
  }, [dispatch]);

  // This screen is only reachable after VerifyOtpCode succeeded on step 2.
  if (!phoneNumber || !otpCode) {
    return <Navigate to="/reset-password" replace />;
  }

  const autoLogin = async (newPassword) => {
    try {
      const res = await axios.post("/api/UsersAuth/LogIn", {
        phoneNumber,
        password: newPassword,
        Password: newPassword,
      });
      if (res.status === 200) {
        Cookies.set("webAuth", res.data.token, { expires: 7 });
        Cookies.set("Customer", JSON.stringify(res.data), { expires: 7 });
        dispatch(setAuth(true));
        navigate("/shop");
        return;
      }
    } catch (e) {
      // fall through to the login page
    }
    navigate("/login");
  };

  const onSubmit = async ({ newPassword, confirmNewPassword }) => {
    setErrorKey("");
    setSubmitting(true);
    const res = await resetPassword(
      phoneNumber,
      otpCode,
      newPassword,
      confirmNewPassword
    );
    if (res.ok && res.code === "PASSWORD_UPDATED") {
      setDone(true);
      await autoLogin(newPassword);
      return;
    }
    setSubmitting(false);
    // The code is single-use/expired: send the user back to start over.
    if (res.code === "INVALID_OTP" || res.code === "OTP_EXPIRED") {
      navigate("/reset-password", { replace: true });
      return;
    }
    setErrorKey(errorKeyFromCode(res.code));
  };

  return (
    <div className="m-0 text-gray-600 bg-white body-font">
      <div className="flex flex-wrap items-center mx-auto ">
        <div className="w-3/4 px-4 py-10 mx-auto text-white lg:mx-2 lg:max-w-xl rounded-3xl bg-gradient-to-t from-pink-700 via-pink-700 to-pink-600 sm:px-10 md:m-6 md:mr-8">
          <p className={`mb-20 font-bold tracking-wider ${locale}`}>{t("vary")}</p>
          <p
            className={`mb-4 text-3xl font-bold md:text-4xl md:leading-snug ${locale}`}
          >
            {t("loginPageMessage")}
          </p>
          <p className={`mb-28 leading-relaxed text-gray-200 ${locale}`}>
            {t("loginPageMessageDetail")}
          </p>
          <div className="px-4 py-8 bg-pink-600/80 rounded-2xl">
            <div className="flex items-center">
              <p className={`${locale === "en-US" ? "ml-4" : "mr-4"} w-56`}>
                {locale === "en-US" ? (
                  <span className={` text-gray-200 font-medium ${locale}`}>
                    {t("varyPharmacy")}
                  </span>
                ) : (
                  <>
                    <span
                      className={`text-xs text-gray-200 font-medium ${locale}`}
                    >
                      {t("pharmacy")}{" "}
                    </span>
                    <strong className={`block font-medium ${locale}`}>
                      {t("vary")}
                    </strong>
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-col w-3/4 mx-auto my-3 bg-white rounded-lg shadow-2xl lg:flex lg:w-2/5">
          <div className="mx-2 my-6 text-center">
            <h1 className={`text-3xl font-semibold text-gray-800 ${locale}`}>
              {t("changePassword")}
            </h1>
            <p className={`mt-2 font-medium text-gray-700 ${locale}`}>
              {t("dontHaveAccount")}{" "}
              <Link
                to="/register"
                className={`text-pink-500 cursor-pointer hover:text-pink-700 ${locale}`}
              >
                {t("createAccount")}
              </Link>
            </p>
          </div>
          <div className="pb-8 mx-6">
            <form className="mb-4" onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="mb-6">
                <label
                  htmlFor="newPassword"
                  className={`block mb-2 text-sm text-gray-600 ${locale}`}
                >
                  {t("newPassword")}
                </label>
                <input
                  type="password"
                  id="newPassword"
                  autoComplete="new-password"
                  placeholder={t("passwordPlaceholder")}
                  className={inputClass(errors.newPassword, locale)}
                  {...register("newPassword", {
                    required: "otpErrPasswordTooShort",
                    minLength: {
                      value: MIN_PASSWORD_LENGTH,
                      message: "otpErrPasswordTooShort",
                    },
                  })}
                />
                {errors.newPassword && (
                  <p role="alert" className={`mt-2 text-sm text-rose-600 ${locale}`}>
                    {t(errors.newPassword.message)}
                  </p>
                )}
              </div>
              <div className="mb-6">
                <label
                  htmlFor="confirmNewPassword"
                  className={`block mb-2 text-sm text-gray-600 ${locale}`}
                >
                  {t("confirmPassword")}
                </label>
                <input
                  type="password"
                  id="confirmNewPassword"
                  autoComplete="new-password"
                  placeholder={t("passwordPlaceholder")}
                  className={inputClass(errors.confirmNewPassword, locale)}
                  {...register("confirmNewPassword", {
                    required: "otpErrPasswordMismatch",
                    validate: (v) =>
                      v === getValues("newPassword") || "otpErrPasswordMismatch",
                  })}
                />
                {errors.confirmNewPassword && (
                  <p role="alert" className={`mt-2 text-sm text-rose-600 ${locale}`}>
                    {t(errors.confirmNewPassword.message)}
                  </p>
                )}
              </div>
              {errorKey && (
                <p role="alert" className={`mb-4 text-sm text-rose-600 ${locale}`}>
                  {t(errorKey)}
                </p>
              )}
              <div className="mb-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`w-full px-3 py-3 text-lg font-medium text-white duration-100 ease-in-out bg-pink-600 rounded-xl hover:bg-pink-400 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed ${locale}`}
                >
                  {t("submit")}
                </button>
              </div>
            </form>
            {done && <DynamicSnackBar />}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MyApp;
